import React, {
 createContext,
 useContext,
 useState,
 useEffect,
 useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import { api } from "../services/api";
import { Cart } from "../types/cart";

interface DesignSelection {
  customizationId: string;
  quantity: number;
}

interface CartContextType {
 cart: Cart | null;
 loading: boolean;
 error: string | null;
 itemCount: number;
 addToCart: (
  productId: string,
  quantity?: number,
  customizationId?: string,
  selectedVariantOptions?: Array<{ optionName: string; optionValue: string }>
 ) => Promise<void>;
 addMultipleDesignsToCart: (
  productId: string,
  designs: DesignSelection[],
  selectedVariantOptions?: Array<{ optionName: string; optionValue: string }>
 ) => Promise<void>;
 updateItem: (itemId: string, quantity: number) => Promise<void>;
 removeItem: (itemId: string) => Promise<void>;
 clearCart: () => Promise<void>;
 refreshCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({
 children,
}) => {
 const { isAuthenticated, user } = useAuth();
 const [cart, setCart] = useState<Cart | null>(null);
 const [loading, setLoading] = useState<boolean>(false);
 const [error, setError] = useState<string | null>(null);

 // Fetch cart from API
 const fetchCart = useCallback(async () => {
  try {
   if (!api) return;

   setLoading(true);
   setError(null);

   const response = await api.get("/cart");
   setCart(response.data);
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to load cart");
   console.error("Error fetching cart:", err);
  } finally {
   setLoading(false);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [api]);

 // Merge guest cart with user cart on login
 useEffect(() => {
  const mergeCarts = async () => {
   if (isAuthenticated) {
    try {
     await api.post("/cart/merge");
     // Refresh the cart to get the merged version
     await fetchCart();
    } catch (error) {
     console.error("Error merging carts:", error);
    }
   }
  };

  mergeCarts();
 }, [isAuthenticated, fetchCart, user?._id]);

 // Initial cart fetch
 useEffect(() => {
  fetchCart();
 }, [fetchCart]);

 // Add item to cart
 const addToCart = async (
  productId: string,
  quantity: number = 1,
  customizationId?: string,
  selectedVariantOptions?: Array<{ optionName: string; optionValue: string }>
 ) => {
  try {
   setLoading(true);
   setError(null);

   const response = await api.post("/cart/items", {
    productId,
    quantity,
    customizationId,
    selectedVariantOptions,
   });

   setCart(response.data);
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to add item to cart");
   console.error("Error adding to cart:", err);
   throw err;
  } finally {
   setLoading(false);
  }
 };

 // Add multiple designs to cart
 const addMultipleDesignsToCart = async (
  productId: string,
  designs: DesignSelection[],
  selectedVariantOptions?: Array<{ optionName: string; optionValue: string }>
 ) => {
  try {
   setLoading(true);
   setError(null);

   const response = await api.post("/cart/items/multiple", {
    productId,
    designs,
    selectedVariantOptions,
   });

   setCart(response.data);
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to add designs to cart");
   console.error("Error adding designs to cart:", err);
   throw err;
  } finally {
   setLoading(false);
  }
 };

 // Update cart item quantity
 const updateItem = async (itemId: string, quantity: number) => {
  try {
   if (!cart) return;

   setLoading(true);
   setError(null);

   const response = await api.put(`/cart/items/${itemId}`, {
    quantity,
   });

   setCart(response.data);
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to update cart item");
   console.error("Error updating cart item:", err);
   throw err;
  } finally {
   setLoading(false);
  }
 };

 // Remove item from cart
 const removeItem = async (itemId: string) => {
  try {
   if (!cart) return;

   setLoading(true);
   setError(null);

   const response = await api.delete(`/cart/items/${itemId}`);
   setCart(response.data);
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to remove item from cart");
   console.error("Error removing cart item:", err);
   throw err;
  } finally {
   setLoading(false);
  }
 };

 // Clear cart
 const clearCart = async () => {
  try {
   if (!cart) return;

   setLoading(true);
   setError(null);

   const response = await api.delete("/cart/clear");
   setCart(response.data);
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to clear cart");
   console.error("Error clearing cart:", err);
   throw err;
  } finally {
   setLoading(false);
  }
 };

 // Calculate total number of items in cart
 const itemCount =
  cart?.items?.reduce((total, item) => total + item.quantity, 0) || 0;

 return (
  <CartContext.Provider
   value={{
    cart,
    loading,
    error,
    itemCount,
    addToCart,
    addMultipleDesignsToCart,
    updateItem,
    removeItem,
    clearCart,
    refreshCart: fetchCart,
   }}
  >
   {children}
  </CartContext.Provider>
 );
};

export const useCart = () => {
 const context = useContext(CartContext);
 if (context === undefined) {
  throw new Error("useCart must be used within a CartProvider");
 }
 return context;
};
